import React, { useEffect, useMemo, useState } from 'react';
import { Settings, Satellite, FileText, Lock, FileCheck2, UserX } from 'lucide-react';
import ProgressBarRemaining from './ProgressBarRemaining';
import usePotvrzeni from '../ui/usePotvrzeni';

const SECTION_COLOR = '#0CC0DF';
// STK platnost typicky 2 roky = 730 dní. Baseline pro výpočet % progress baru.
const STK_BASELINE_DNI = 730;

// POZOR: Field i SectionHeader MUSÍ být mimo parent funkci. Definovat dovnitř
// = nová ref na každý render → React unmount/remount celý subtree → input
// ztratí focus → kurzor "skáče" na první pole.
function Field({ label, hint, children, labelColor }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{
                fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.5,
                color: labelColor,
            }}>
                {label}
            </span>
            {children}
            {hint && <span style={{ fontSize: 10, color: labelColor, lineHeight: 1.4 }}>{hint}</span>}
        </label>
    );
}

function SectionHeader({ icon: Icon, color, label }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 11, color, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.5,
            paddingTop: 4,
        }}>
            <Icon size={11} /> {label}
        </div>
    );
}

/**
 * Sub-tab Nastavení v Inspectoru. Stejný layout jako InspectorCreate (přidání
 * vozidla), ale rozdělen na dvě sekce:
 *   - Nastavení = co admin manuálně nastavuje (SPZ, Traccar device, servisní
 *     interval, poznámka).
 *   - Detail (Datová kostka) = read-only data z registru MD ČR — model, rok,
 *     palivo, typ, VIN/ORV/TP. Pokud potřebuješ změnit, dělá se to přes
 *     re-lookup v InspectorCreate, ne ručně tady.
 */
export default function InspectorNastaveni({ vozidlo, onSave, isDark }) {
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);
    const [form, setForm] = useState({
        spz: vozidlo?.spz || '',
        traccar_device_id: vozidlo?.traccar_device_id || '',
        km_servis_interval: vozidlo?.km_servis_interval || '',
        poznamka: vozidlo?.poznamka || '',
        stk_platnost_do: vozidlo?.stk_platnost_do || '',
    });
    const [ciselniky, setCiselniky] = useState({ typy_vozidel: [], typy_paliv: [] });
    const [traccarDevices, setTraccarDevices] = useState([]);
    const [loadingTraccar, setLoadingTraccar] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [error, setError] = useState(null);
    const [uvolnujiRidice, setUvolnujiRidice] = useState(false);

    useEffect(() => {
        fetch('/api/flotila/ciselniky').then((r) => r.json()).then(setCiselniky).catch(() => {});
        setLoadingTraccar(true);
        fetch('/api/flotila/gps/traccar-devices')
            .then((r) => r.json())
            .then((d) => setTraccarDevices(Array.isArray(d) ? d : []))
            .catch(() => {})
            .finally(() => setLoadingTraccar(false));
    }, []);

    // Re-sync form pokud se vozidlo prop změní (přepnutí auta v inspectoru)
    useEffect(() => {
        setForm({
            spz: vozidlo?.spz || '',
            traccar_device_id: vozidlo?.traccar_device_id || '',
            km_servis_interval: vozidlo?.km_servis_interval || '',
            poznamka: vozidlo?.poznamka || '',
            stk_platnost_do: vozidlo?.stk_platnost_do || '',
        });
        setSavedAt(null);
        setError(null);
    }, [vozidlo?.id]);

    const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const uvolnitRidice = async () => {
        if (!vozidlo?.id) return;
        const ridicJmeno = vozidlo?.aktualni_ridic
            ? `${vozidlo.aktualni_ridic.jmeno} ${vozidlo.aktualni_ridic.prijmeni}`
            : 'řidič';
        if (!(await potvrdit(`Uvolnit auto od ${ridicJmeno}?`, { text: 'Auto se začne nabízet v pickeru. Otevřené směny s tímto autem se označí jako vrácené.', potvrdit: 'Uvolnit' }))) return;
        setUvolnujiRidice(true);
        setError(null);
        try {
            const csrf = document.querySelector('meta[name="csrf-token"]')?.content || '';
            const r = await fetch(`/api/flotila/vozidla/${vozidlo.id}/uvolnit-ridice`, {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': csrf, 'Accept': 'application/json' },
                credentials: 'same-origin',
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                throw new Error(data?.error || 'Uvolnění selhalo');
            }
            onSave?.();
        } catch (err) {
            setError(err.message || 'Uvolnění selhalo');
        } finally {
            setUvolnujiRidice(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!vozidlo?.id) return;
        setSaving(true);
        setError(null);
        try {
            const csrf = document.querySelector('meta[name="csrf-token"]')?.content || '';
            const headers = {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrf,
                'Accept': 'application/json',
            };

            const r = await fetch(`/api/flotila/vozidla/${vozidlo.id}`, {
                method: 'PUT',
                headers,
                credentials: 'same-origin',
                body: JSON.stringify({
                    spz: form.spz,
                    poznamka: form.poznamka,
                    km_servis_interval: form.km_servis_interval ? Number(form.km_servis_interval) : null,
                    traccar_device_id: form.traccar_device_id || null,
                }),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                throw new Error(data?.message || data?.error || 'Uložení selhalo');
            }

            // STK doklad — pokud user změnil datum, upsert. Hodnota '' = beze změny
            // (neumažeme náhodou existující datum; mazání by chtělo separátní akci).
            const novaSTK = form.stk_platnost_do || '';
            const puvodniSTK = vozidlo?.stk_platnost_do || '';
            if (novaSTK && novaSTK !== puvodniSTK) {
                const dokladId = vozidlo?.stk_doklad_id;
                const url = dokladId
                    ? `/api/flotila/doklady/${dokladId}`
                    : `/api/flotila/vozidla/${vozidlo.id}/doklady`;
                const method = dokladId ? 'PUT' : 'POST';
                const stkBody = dokladId
                    ? { platnost_do: novaSTK }
                    : { typ: 'stk', nazev: 'STK (manuálně)', platnost_do: novaSTK };
                const stkRes = await fetch(url, {
                    method, headers,
                    credentials: 'same-origin',
                    body: JSON.stringify(stkBody),
                });
                if (!stkRes.ok) {
                    const data = await stkRes.json().catch(() => ({}));
                    throw new Error(data?.message || data?.error || 'STK uložení selhalo');
                }
            }

            setSavedAt(new Date());
            onSave?.();
        } catch (err) {
            setError(err.message || 'Uložení selhalo');
        } finally {
            setSaving(false);
        }
    };

    const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const subtle = isDark ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)';
    const textColor = isDark ? '#fff' : 'var(--text-primary)';
    const inputBorder = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const inputBg = isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-base)';

    const inputStyle = {
        width: '100%',
        background: inputBg,
        color: textColor,
        border: `1px solid ${inputBorder}`,
        borderRadius: 7,
        padding: '8px 10px',
        fontSize: 13,
        outline: 'none',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    };
    const monoStyle = { ...inputStyle, fontVariantNumeric: 'tabular-nums', letterSpacing: 0.3 };
    const disabledStyle = {
        ...inputStyle,
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
        color: subtle,
        cursor: 'not-allowed',
    };
    const disabledMono = { ...disabledStyle, fontVariantNumeric: 'tabular-nums', letterSpacing: 0.3 };

    const typVozidlaNazev = ciselniky.typy_vozidel?.find((t) => t.id === vozidlo?.typ_vozidla_id)?.nazev
        || vozidlo?.typ_vozidla || '';
    const typPalivaNazev = ciselniky.typy_paliv?.find((t) => t.id === vozidlo?.typ_paliva_id)?.nazev
        || vozidlo?.typ_paliva || '';

    // Pokud Traccar API není dostupné (např. na staging serveru, kde Traccar
    // neběží), endpoint vrací []. Aby uživatel pořád viděl,
    // jaký tracker má vozidlo přiřazený, doplníme synthetic option z DB hodnoty.
    const traccarUnavailable = traccarDevices.length === 0 && !loadingTraccar;
    const devicesForSelect = useMemo(() => {
        if (!form.traccar_device_id) return traccarDevices;
        const exists = traccarDevices.some((d) => String(d.id) === String(form.traccar_device_id));
        if (exists) return traccarDevices;
        return [
            ...traccarDevices,
            {
                id: form.traccar_device_id,
                name: `Tracker #${form.traccar_device_id}`,
                status: null,
                assigned_to: vozidlo?.id,
                assigned_spz: vozidlo?.spz,
                _synthetic: true,
            },
        ];
    }, [traccarDevices, form.traccar_device_id, vozidlo]);

    const traccarSelected = devicesForSelect.find((d) => String(d.id) === String(form.traccar_device_id));
    const traccarConflict = traccarSelected && !traccarSelected._synthetic
        && traccarSelected.assigned_to && traccarSelected.assigned_to !== vozidlo?.id;

    const aktualniRidicJmeno = vozidlo?.aktualni_ridic
        ? `${vozidlo.aktualni_ridic.jmeno} ${vozidlo.aktualni_ridic.prijmeni}`
        : null;

    return (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {potvrzeni}
            {/* === AKTUÁLNÍ ŘIDIČ — manuální uvolnění === */}
            {vozidlo?.aktualni_ridic_id && (
                <div style={{
                    padding: 12, borderRadius: 9,
                    background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.05)',
                    border: `1px solid ${isDark ? 'rgba(239,68,68,0.30)' : 'rgba(239,68,68,0.30)'}`,
                    display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                    <SectionHeader icon={UserX} color="#EF4444" label="Aktuální řidič" />
                    <div style={{ fontSize: 13, color: textColor, lineHeight: 1.5 }}>
                        Auto je přiřazené řidiči{aktualniRidicJmeno ? <> <strong>{aktualniRidicJmeno}</strong></> : ''}.
                    </div>
                    <div style={{ fontSize: 11, color: labelColor, lineHeight: 1.4 }}>
                        Uvolnit ručně, pokud řidič zapomněl ukončit směnu nebo vrátit auto. Auto se hned objeví v pickeru pro převzetí.
                    </div>
                    <div>
                        <button
                            type="button"
                            onClick={uvolnitRidice}
                            disabled={uvolnujiRidice}
                            style={{
                                background: 'rgba(239,68,68,0.12)',
                                color: '#EF4444',
                                border: '1px solid rgba(239,68,68,0.40)',
                                padding: '7px 14px',
                                borderRadius: 7,
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: uvolnujiRidice ? 'wait' : 'pointer',
                                opacity: uvolnujiRidice ? 0.6 : 1,
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                            }}
                        >
                            <UserX size={13} /> {uvolnujiRidice ? 'Uvolňuji…' : 'Ukončit manuálně'}
                        </button>
                    </div>
                </div>
            )}

            {/* === NASTAVENÍ === */}
            <div style={{
                padding: 12, borderRadius: 9,
                background: isDark ? 'rgba(12,192,223,0.06)' : 'rgba(12,192,223,0.04)',
                border: `1px solid ${isDark ? 'rgba(12,192,223,0.20)' : 'rgba(12,192,223,0.25)'}`,
                display: 'flex', flexDirection: 'column', gap: 10,
            }}>
                <SectionHeader icon={Settings} color={SECTION_COLOR} label="Nastavení" />
                <Field label="SPZ" labelColor={labelColor}>
                    <input style={monoStyle} value={form.spz} onChange={change('spz')} maxLength={10} required />
                </Field>
                <Field label="Servisní interval (km)" hint="Po této vzdálenosti od posledního servisu naskočí varování v dlaždici i kartě." labelColor={labelColor}>
                    <input style={monoStyle} type="number" min="0"
                        value={form.km_servis_interval} onChange={change('km_servis_interval')} />
                </Field>
                {vozidlo?.km_do_servisu !== null && vozidlo?.km_do_servisu !== undefined && vozidlo?.km_servis_interval > 0 && (
                    <ProgressBarRemaining
                        label={vozidlo.km_do_servisu < 0
                            ? `Servis o ${Math.abs(vozidlo.km_do_servisu).toLocaleString('cs-CZ')} km přes interval`
                            : `Do servisu zbývá ${vozidlo.km_do_servisu.toLocaleString('cs-CZ')} km`}
                        remaining={vozidlo.km_do_servisu} total={vozidlo.km_servis_interval}
                        isDark={isDark}
                    />
                )}
            </div>

            {/* === STK === */}
            <div style={{
                padding: 12, borderRadius: 9,
                background: isDark ? 'rgba(255,200,87,0.06)' : 'rgba(255,200,87,0.05)',
                border: `1px solid ${isDark ? 'rgba(255,200,87,0.25)' : 'rgba(255,200,87,0.30)'}`,
                display: 'flex', flexDirection: 'column', gap: 8,
            }}>
                <SectionHeader icon={FileCheck2} color="#FFC857" label="STK" />
                <Field
                    label="Platnost do"
                    labelColor={labelColor}
                    hint={
                        vozidlo?.vin
                            ? 'Auto se denně sync-uje z Datové kostky MD ČR podle VIN. Manuální změna přepíše hodnotu z registru až do dalšího běhu cronu.'
                            : 'Bez VIN se STK auto-sync neprovádí — zadej datum manuálně.'
                    }
                >
                    <input style={inputStyle} type="date"
                        value={form.stk_platnost_do} onChange={change('stk_platnost_do')} />
                </Field>
                {vozidlo?.stk_dni_do_expirace !== null && vozidlo?.stk_dni_do_expirace !== undefined && (
                    <ProgressBarRemaining
                        label={vozidlo.stk_dni_do_expirace < 0
                            ? `STK expirovala před ${Math.abs(vozidlo.stk_dni_do_expirace)} dny`
                            : vozidlo.stk_dni_do_expirace === 0
                            ? 'STK expiruje dnes'
                            : `STK platná ještě ${vozidlo.stk_dni_do_expirace} dní`}
                        remaining={vozidlo.stk_dni_do_expirace} total={STK_BASELINE_DNI}
                        isDark={isDark}
                    />
                )}
            </div>

            {/* === TRACCAR === */}
            <div style={{
                padding: 12, borderRadius: 9,
                background: isDark ? 'rgba(151,90,205,0.07)' : 'rgba(151,90,205,0.05)',
                border: `1px solid ${isDark ? 'rgba(151,90,205,0.25)' : 'rgba(151,90,205,0.30)'}`,
                display: 'flex', flexDirection: 'column', gap: 8,
            }}>
                <SectionHeader icon={Satellite} color="#975ACD" label="Traccar GPS tracker" />
                <Field
                    label="Zařízení"
                    labelColor={labelColor}
                    hint={
                        loadingTraccar ? 'Načítám zařízení z Traccaru…'
                        : traccarUnavailable ? 'Traccar API z této instance nedosáhne (typicky když Traccar běží za interní sítí). Aktuální přiřazení vidíš, ale výběr jiných zařízení tady nelze.'
                        : 'Volné zařízení = bez vozidla. Změnou vozidlu odebereš tracker původnímu vozidlu.'
                    }
                >
                    <select
                        style={inputStyle}
                        value={form.traccar_device_id}
                        onChange={change('traccar_device_id')}
                        disabled={traccarUnavailable && !!form.traccar_device_id}
                    >
                        <option value="">— Bez Traccaru —</option>
                        {devicesForSelect.map((d) => {
                            const obsazeno = d._synthetic
                                ? ' · TOTO vozidlo (Traccar API nedostupné)'
                                : d.assigned_to
                                    ? (d.assigned_to === vozidlo?.id ? ' · TOTO vozidlo' : ` · ${d.assigned_spz || 'přiřazeno'}`)
                                    : ' · Volné';
                            return (
                                <option key={d.id} value={d.id}>
                                    {d.name || d.id}{obsazeno}{d.status ? ` · ${d.status}` : ''}
                                </option>
                            );
                        })}
                    </select>
                </Field>
                {traccarConflict && (
                    <div style={{
                        fontSize: 11, padding: '6px 10px', borderRadius: 6,
                        background: 'rgba(255,200,87,0.10)',
                        color: '#FFC857',
                        border: '1px solid rgba(255,200,87,0.25)',
                    }}>
                        ⚠ Tracker je přiřazený k vozidlu {traccarSelected.assigned_spz}. Uložením se přesune sem.
                    </div>
                )}
            </div>

            {/* === POZNÁMKA === */}
            <Field label="Poznámka" labelColor={labelColor}>
                <textarea
                    style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                    value={form.poznamka}
                    onChange={change('poznamka')}
                />
            </Field>

            {/* === DETAIL (DATOVÁ KOSTKA) === */}
            <div style={{
                padding: 12, borderRadius: 9,
                background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                border: `1px dashed ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                display: 'flex', flexDirection: 'column', gap: 10,
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 11, color: subtle, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                    <Lock size={11} /> Detail (z registru MD ČR)
                </div>
                <div style={{ fontSize: 11, color: labelColor, lineHeight: 1.4, marginTop: -4 }}>
                    Tato data se načítají z Datové kostky — měnit je tady nelze. Pro re-lookup smaž a založ vozidlo znovu.
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Model" labelColor={labelColor}>
                        <input style={disabledStyle} value={vozidlo?.model || ''} disabled />
                    </Field>
                    <Field label="Rok výroby" labelColor={labelColor}>
                        <input style={disabledMono} value={vozidlo?.rok_vyroby || ''} disabled />
                    </Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Typ vozidla" labelColor={labelColor}>
                        <input style={disabledStyle} value={typVozidlaNazev} disabled />
                    </Field>
                    <Field label="Typ paliva" labelColor={labelColor}>
                        <input style={disabledStyle} value={typPalivaNazev} disabled />
                    </Field>
                </div>
                <Field label="Barva" labelColor={labelColor}>
                    <input style={disabledStyle} value={vozidlo?.barva || ''} disabled />
                </Field>
                <Field label="VIN" labelColor={labelColor}>
                    <input style={disabledMono} value={vozidlo?.vin || ''} disabled />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="ORV" labelColor={labelColor}>
                        <input style={disabledMono} value={vozidlo?.orv || ''} disabled />
                    </Field>
                    <Field label="TP" labelColor={labelColor}>
                        <input style={disabledMono} value={vozidlo?.tp || ''} disabled />
                    </Field>
                </div>
            </div>

            {error && (
                <div style={{
                    padding: '8px 12px', borderRadius: 7, fontSize: 12,
                    background: 'rgba(239,68,68,0.12)', color: '#EF4444',
                    border: '1px solid rgba(239,68,68,0.30)',
                }}>
                    {error}
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                <button
                    type="submit"
                    disabled={saving}
                    style={{
                        background: 'linear-gradient(135deg, #1D9E75, #10B981)',
                        color: '#fff',
                        border: 'none',
                        padding: '9px 18px',
                        borderRadius: 7,
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: saving ? 'wait' : 'pointer',
                        opacity: saving ? 0.6 : 1,
                    }}
                >
                    {saving ? 'Ukládám…' : 'Uložit'}
                </button>
                {savedAt && (
                    <span style={{ fontSize: 11, color: '#10B981' }}>
                        ✓ Uloženo {savedAt.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                )}
            </div>
        </form>
    );
}
